// Reusable KPI card.
//
// A compact, dashboard-style card showing a single metric: a label
// (small, uppercase), a large number, and an optional sub-label and
// icon. Used across modules where a row of KPI cards summarises the
// state of the current view (rosters, training compliance, mechanical
// movements, etc.).
//
// Design notes:
//   - White background with a subtle coloured border + dot for tint —
//     keeps the cards calm against page chrome but lets a glance
//     differentiate categories.
//   - `onClick` makes the card a button (cursor + hover lift).
//   - Number renders large + bold so it's the dominant element.
//   - Sub-label is muted and optional — drop it when there's no
//     useful qualifier.

import type { ElementType, ReactNode } from 'react';

export type KpiCardTint =
  | 'blue'
  | 'emerald'
  | 'amber'
  | 'rose'
  | 'slate'
  | 'violet';

interface KpiCardProps {
  label: string;
  value: number | string;
  sublabel?: string;
  icon?: ElementType;
  /** Tint colour for the accent dot, border, and number. Defaults to slate. */
  tint?: KpiCardTint;
  /** Optional click target — makes the card a button. */
  onClick?: () => void;
  /** Optional right-side trailing content (badge, trend arrow, etc.). */
  trailing?: ReactNode;
  /** Suppresses the value formatting (e.g. for already-formatted strings). */
  raw?: boolean;
}

const TINT: Record<
  KpiCardTint,
  {
    border: string;
    value: string;
    dot: string;
    iconBg: string;
    iconFg: string;
  }
> = {
  blue: {
    border: 'border-blue-200/70',
    value: 'text-blue-700',
    dot: 'bg-blue-500',
    iconBg: 'bg-blue-50',
    iconFg: 'text-blue-600',
  },
  emerald: {
    border: 'border-green-200/70',
    value: 'text-green-700',
    dot: 'bg-green-500',
    iconBg: 'bg-green-50',
    iconFg: 'text-green-600',
  },
  amber: {
    border: 'border-amber-200/70',
    value: 'text-amber-700',
    dot: 'bg-amber-500',
    iconBg: 'bg-amber-50',
    iconFg: 'text-amber-600',
  },
  rose: {
    border: 'border-rose-200/70',
    value: 'text-rose-700',
    dot: 'bg-rose-500',
    iconBg: 'bg-rose-50',
    iconFg: 'text-rose-600',
  },
  slate: {
    border: 'border-gray-200',
    value: 'text-gray-800',
    dot: 'bg-gray-400',
    iconBg: 'bg-gray-50',
    iconFg: 'text-gray-600',
  },
  violet: {
    border: 'border-violet-200/70',
    value: 'text-violet-700',
    dot: 'bg-violet-500',
    iconBg: 'bg-violet-50',
    iconFg: 'text-violet-600',
  },
};

export default function KpiCard({
  label,
  value,
  sublabel,
  icon: Icon,
  tint = 'slate',
  onClick,
  trailing,
  raw,
}: KpiCardProps) {
  const t = TINT[tint];
  const display =
    raw || typeof value === 'string' ? value : value.toLocaleString('en-AU');
  return (
    <div
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      // A role="button" must answer the keys a button does.
      onKeyDown={
        onClick
          ? e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      className={`rounded-lg border ${t.border} bg-white px-3 py-2 shadow-sm flex items-center gap-3 ${
        onClick
          ? 'cursor-pointer hover:shadow-md transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2'
          : ''
      }`}
    >
      {Icon && (
        <div
          className={`h-9 w-9 rounded-md ${t.iconBg} ${t.iconFg} flex items-center justify-center flex-shrink-0`}
        >
          <Icon className="h-5 w-5" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          {!Icon && (
            <span className={`inline-block h-1.5 w-1.5 rounded-full ${t.dot}`} aria-hidden />
          )}
          <span className="text-2xs font-bold uppercase tracking-wider text-gray-500 truncate">
            {label}
          </span>
        </div>
        {/* A long sublabel wraps under the figure rather than being cut off. */}
        <div className="flex flex-wrap items-baseline gap-x-1.5">
          <span className={`text-2xl font-bold leading-none ${t.value}`}>{display}</span>
          {sublabel && <span className="text-2xs text-gray-500">{sublabel}</span>}
        </div>
      </div>
      {trailing}
    </div>
  );
}
